import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  NOTIFICATIONS_QUERY_KEY,
  clearAllNotifications,
  deleteNotification,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/services/notificationService"
import { useCurrentUser } from "./useCurrentUser"
import type { NotificationItem } from "@/types"

export function useNotifications() {
  const queryClient = useQueryClient()
  const { data: currentUser } = useCurrentUser()

  // Get notifications from current user data (already fetched via /auth/user/me)
  const notifications = (currentUser?.notifications ?? []) as NotificationItem[]
  const invalidateNotifications = () => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
    void queryClient.invalidateQueries({ queryKey: ["auth", "me"] })
  }

  const deleteOne = useMutation({
    mutationFn: (id: string) => deleteNotification(id),
    onSuccess: invalidateNotifications,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => markNotificationRead(id),
    onSuccess: invalidateNotifications,
  })

  const markAllRead = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: invalidateNotifications,
  })

  const clearAll = useMutation({
    mutationFn: clearAllNotifications,
    onSuccess: invalidateNotifications,
  })

  return {
    data: notifications,
    isLoading: false,
    isError: false,
    error: null,
    notifications,
    unreadCount: notifications.filter((n) => !(n as any).is_read).length,
    deleteOne,
    markRead,
    markAllRead,
    clearAll,
    refetch: () => queryClient.invalidateQueries({ queryKey: ["auth", "me"] }),
  }
}
